"use client"

/**
 * Finance Setup (IBA-119) — the branch's accounting identity.
 *
 * Currency, fiscal-year start and the conversion date are not preferences: they decide what the
 * numbers MEAN. Until a church states them, the backend refuses to record money at all — the gate lives at the
 * ledger, so a deep link or a stale tab cannot slip past it.
 *
 * They lock on the LEDGER, not on the setup flag: once the branch has posted, these values are what its
 * existing entries mean, so changing them would restate history rather than describe it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { financeQueryKeys } from "@/features/finance/services/finance-query-keys"

import { Currency } from "@ibada/shared-types/finance"
import { AccountingTimezone } from "@ibada/shared-types/period"

import type { AuthMeResponse } from "@/features/auth/model/auth-types"
import { useAuthContextStore } from "@/features/auth/services/auth-context-store"
import { FinanceGovernance } from "@/features/finance/types/finance-governance"
import { http } from "@/shared/lib/api/http"

export type FinanceSetupStatus = {
  isComplete: boolean
  completedAt: string | null
  baseCurrency: Currency
  fiscalYearStartMonth: number
  /** The day Ibada Cloud became the system of record. Null until setup is completed. */
  conversionDate: string | null
  /** True once the branch has posted: its accounting identity is now history, not a setting. */
  isLocked: boolean
  postedEntries: number
  /** Active financial accounts on this branch — drives the DERIVED "minimum setup complete" gate. */
  financialAccountsCount: number
  // --- cascade (parish → branches) ---
  /** True for the parish/system branch itself. */
  isParish: boolean
  /** A branch's fiscal year is the parish's, org-wide and locked — the wizard renders it read-only. */
  fiscalYearInherited: boolean
  /** A branch cannot complete setup until the parish's is done — the wizard blocks the form on this. */
  parishSetupComplete: boolean
  // --- ADR-0024: how the organization runs its books ---
  /** `FULL_PARISH` (the default, today's behaviour) or `BRANCH_AUTONOMY`. */
  financeGovernance: FinanceGovernance
  /**
   * Whether the choice is still open. Derived server-side from the PARISH's setup, never stored, so a branch
   * reading this sees the org-wide answer rather than its own progress.
   */
  governanceLocked: boolean
  /** Only a multi-branch workspace has anyone to keep books from, so only it is asked. */
  governanceApplies: boolean
  /**
   * Whether anyone has ANSWERED, as opposed to the column still holding its default.
   *
   * `financeGovernance` always has a value, so it cannot say this on its own — which is why the step showed a
   * blank question on a return visit and read as lost work. The server records when the choice was made.
   */
  governanceChosen: boolean
  /**
   * What this caller may actually be SHOWN here (ADR-0024), so a board can render ratios instead of
   * absolutes rather than rendering absolutes as zero — `$0.00` for a branch holding $1,200 is not a smaller
   * truth, it is a different and false one.
   *
   * Two questions because a consolidated board asks a harder one: a total over n branches with n-1
   * disclosable gives up the last by subtraction, so Global needs every branch, not just the one it is on.
   */
  disclosure: {
    /** May this caller see the absolute figures of the branch this status is about? */
    branchAbsolutes: boolean
    /** May this caller see every active branch's — what an honest consolidated total requires? */
    allBranchAbsolutes: boolean
  }
  // --- IBA-170: the second gate (books open for business) ---
  /** True once Complete Finance Setup has been clicked. What the operational ledger actually gates on. */
  isActivated: boolean
  activatedAt: string | null
  /** Total opening money, how much is allocated, and what is left. Null until the identity is set. */
  openingAllocation: {
    currency: string
    totalOpeningMoney: string
    allocated: string
    remaining: string
    isSatisfied: boolean
  } | null
}

export type ActivateFinanceResult = { isActivated: boolean; activatedAt: string | null }

/**
 * The unlock gate, derived ENTIRELY from real accounting data: a branch's finance is usable once its accounting
 * identity is set AND it has told us where its money lives (≥1 financial account). Never derived from the stored
 * wizard step — the wizard is UX; this is the business rule.
 */
export function isMinimumSetupComplete(status: FinanceSetupStatus | undefined | null): boolean {
  return Boolean(status && status.isComplete && status.financialAccountsCount > 0)
}

/**
 * Parish-first setup is a multi-branch cascade only. A mono workspace's one normal branch is the church's
 * finance context, so it must never be blocked by an unconfigured/invisible parish branch.
 */
export function isParishFirstSetupBlocked(
  status: Pick<FinanceSetupStatus, "governanceApplies" | "isParish" | "parishSetupComplete"> | null | undefined,
): boolean {
  return Boolean(status?.governanceApplies && !status.isParish && !status.parishSetupComplete)
}

export type CompleteFinanceSetupInput = {
  /**
   * The accounting currency (ADR-0022 §6) — OPTIONAL. A self-configuring context (parish / mono-branch) may
   * choose it while the ledger is empty; a cascading branch inherits the parish's and omits it. Immutable once
   * anything has posted (the backend refuses a change after that).
   */
  baseCurrency?: Currency
  /**
   * The BRANCH timezone. Optional for older clients; when sent, the backend writes `Branch.timezone` (there is
   * no second finance timezone) and resolves `conversionDate` in that zone.
   */
  timezone?: AccountingTimezone
  fiscalYearStartMonth: number
  /** A calendar day (`YYYY-MM-DD`) — the backend resolves it in the BRANCH's timezone, never in UTC. */
  conversionDate: string
}

/**
 * Keep the in-memory auth branch clock in sync after setup, without refetching `/auth/me`.
 *
 * Completing Step 1 used to invalidate auth and remount the wizard. Patching the stored branch timezone is
 * enough for later finance date math to use the clock the treasurer just chose.
 */
export const applyBranchTimezoneToAuthMe = (
  authMe: AuthMeResponse,
  branchId: string,
  timezone: string,
): AuthMeResponse => ({
  ...authMe,
  workspaceAccess: authMe.workspaceAccess.map((access) => ({
    ...access,
    workspace: {
      ...access.workspace,
      systemBranch:
        access.workspace.systemBranch?.id === branchId
          ? { ...access.workspace.systemBranch, timezone }
          : access.workspace.systemBranch,
      operationalBranch:
        access.workspace.operationalBranch?.id === branchId
          ? { ...access.workspace.operationalBranch, timezone }
          : access.workspace.operationalBranch,
    },
    userBranchAccesses: access.userBranchAccesses?.map((entry) =>
      entry.branchId === branchId && entry.branch
        ? { ...entry, branch: { ...entry.branch, timezone } }
        : entry,
    ),
  })),
})

export const financeSetupKey = (workspaceId: string | null, branchId: string | null) =>
  financeQueryKeys.setupStatus(workspaceId, branchId)

export async function fetchFinanceSetup(workspaceId: string, branchId: string): Promise<FinanceSetupStatus> {
  const response = await http.get<FinanceSetupStatus>("/finance/setup", { params: { workspaceId, branchId } })
  return response.data
}

export async function completeFinanceSetup(
  workspaceId: string,
  branchId: string,
  input: CompleteFinanceSetupInput,
): Promise<FinanceSetupStatus> {
  const response = await http.put<FinanceSetupStatus>("/finance/setup", { workspaceId, branchId, ...input })
  return response.data
}

/**
 * Complete Finance Setup (IBA-170) — open the branch's books.
 *
 * Carries no settings: every value it depends on was stated by the steps before it. The backend refuses while
 * any opening money is still unallocated, which is why the button is disabled rather than optimistic.
 */
export async function activateFinance(workspaceId: string, branchId: string): Promise<ActivateFinanceResult> {
  const response = await http.post<ActivateFinanceResult>("/finance/setup/activate", {
    workspaceId,
    branchId,
  })
  return response.data
}

export function useFinanceSetupQuery(workspaceId: string | null, branchId: string | null) {
  return useQuery({
    queryKey: financeSetupKey(workspaceId, branchId),
    queryFn: () => fetchFinanceSetup(workspaceId as string, branchId as string),
    enabled: Boolean(workspaceId && branchId),
  })
}

/** A branch whose figures the parish side would stop seeing under `BRANCH_AUTONOMY`. */
export type FinanceGovernanceImpactBranch = { id: string; name: string }

export async function fetchFinanceGovernanceImpact(
  workspaceId: string,
): Promise<{ branches: FinanceGovernanceImpactBranch[] }> {
  const response = await http.get<{ branches: FinanceGovernanceImpactBranch[] }>(
    "/finance/setup/governance/impact",
    { params: { workspaceId } },
  )
  return response.data
}

/**
 * What autonomy would cost, asked before it is chosen.
 *
 * Only worth fetching while the choice is still open and only when autonomy is the option in hand, so the
 * caller gates it with `enabled` rather than this hook guessing. The server runs the same query the
 * revocation runs, so the names listed here are exactly the branches that will be withdrawn.
 */
export function useFinanceGovernanceImpactQuery(workspaceId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: financeQueryKeys.governanceImpact(workspaceId),
    queryFn: () => fetchFinanceGovernanceImpact(workspaceId as string),
    enabled: Boolean(workspaceId) && enabled,
  })
}

/**
 * The cached setup status with the activation the server just confirmed written into it.
 *
 * Kept separate so the redirect that follows a completed wizard can be reasoned about on its own: the guard on
 * /finance reads `isActivated` out of this cache, and if it still says false the treasurer is sent straight
 * back to the wizard they just finished.
 */
export const applyActivationToStatus = (
  current: FinanceSetupStatus | undefined,
  result: ActivateFinanceResult,
): FinanceSetupStatus | undefined =>
  current ? { ...current, isActivated: result.isActivated, activatedAt: result.activatedAt } : current

export function useActivateFinanceMutation(workspaceId: string | null, branchId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => activateFinance(workspaceId as string, branchId as string),
    onSuccess: (result) => {
      /*
       * Write the server's own answer into the cache BEFORE anything navigates.
       *
       * Completing the wizard leaves for /finance, and `FinanceSetupGuard` there reads this exact query to
       * decide whether the branch still needs setting up. Invalidation alone is fire-and-forget — a bare
       * `invalidateQueries` in this callback is not awaited by `mutateAsync` — so the guard was reading the
       * status from before the activation, finding `isActivated: false`, and redirecting the treasurer
       * straight back onto the wizard they had just finished. The activation had succeeded; it simply did
       * not look like it. Pressing Complete a second time then "worked", because by then the refetch had
       * landed and the second press only had to navigate.
       *
       * A patch rather than an await: the response carries the two fields that changed, so the redirect
       * target can be made correct synchronously instead of racing a refetch.
       */
      queryClient.setQueryData<FinanceSetupStatus>(financeSetupKey(workspaceId, branchId), (current) =>
        applyActivationToStatus(current, result),
      )
      // Activation unlocks the entire operational ledger — every finance surface was, until this moment,
      // being refused by the posting gate. Root-wide is exactly what this write changed (ADR-0007).
      queryClient.invalidateQueries({ queryKey: financeQueryKeys.all })
    },
  })
}

export function useCompleteFinanceSetupMutation(workspaceId: string | null, branchId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CompleteFinanceSetupInput) =>
      completeFinanceSetup(workspaceId as string, branchId as string, input),
    onSuccess: (status, input) => {
      queryClient.setQueryData(financeSetupKey(workspaceId, branchId), status)
      if (branchId && input.timezone) {
        const current = useAuthContextStore.getState().authMe
        if (current) {
          useAuthContextStore.getState().setAuthMe(applyBranchTimezoneToAuthMe(current, branchId, input.timezone))
        }
      }
      // Completing setup unlocks the whole module — the calendar it states decides which dates accept a
      // posting, so every finance surface is downstream of it. Root-wide is what this write actually changed
      // (ADR-0007); it is the only finance write for which that is true.
      queryClient.invalidateQueries({ queryKey: financeQueryKeys.all })
    },
  })
}

/**
 * Choose how the organization runs its books (ADR-0024).
 *
 * Workspace-wide, so it takes no branch: whether the parish reads every branch's money in full or only their
 * health is a fact about the organization. The server refuses it once the parish has completed setup — the
 * lock is derived there, and the UI's `governanceLocked` is a courtesy, not the guard.
 */
export async function setFinanceGovernance(
  workspaceId: string,
  governance: FinanceGovernance
): Promise<{ financeGovernance: FinanceGovernance }> {
  const response = await http.put<{ financeGovernance: FinanceGovernance }>("/finance/setup/governance", {
    workspaceId,
    governance,
  })
  return response.data
}

export function useSetFinanceGovernanceMutation(workspaceId: string | null, branchId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (governance: FinanceGovernance) => setFinanceGovernance(workspaceId as string, governance),
    onSuccess: () => {
      // Governance decides what every finance response is allowed to carry, so nothing already cached is
      // still trustworthy — root-wide is precisely what this write changed (ADR-0007).
      queryClient.invalidateQueries({ queryKey: financeSetupKey(workspaceId, branchId) })
      queryClient.invalidateQueries({ queryKey: financeQueryKeys.all })
    },
  })
}
